import React, { useEffect, useState } from 'react';
import { IBitComment } from 'types/src/types';
import { CommentItem } from './CommentItem';
import { cn } from '@workspace/ui/lib/utils';
import { Button } from '@workspace/ui/components/button';
import { getBitComments, getCommentReplies } from '@/src/actions/comments';
import { useBoolean } from '@/src/hooks/use-boolean';
import { Spinner } from '@/src/components/ui';

type Props = {
  comments: IBitComment[];
  level?: number;
  onCommentPosted?: () => void;
  replyTo?: number;
};

export const Comments = ({
  comments,
  level = 0,
  onCommentPosted,
  replyTo,
}: Props) => {
  const [commentsList, setCommentsList] = useState<IBitComment[]>(comments);
  const [isMoreAvailable, setIsMoreAvailable] = useState(comments.length >= 5);
  const isLoading = useBoolean(false);

  useEffect(() => {
    setCommentsList(comments);
    setIsMoreAvailable(comments.length >= 5);
  }, [comments]);

  const handleMoreComments = async () => {
    isLoading.onTrue();
    try {
      if (comments[0] && comments[0].bitId) {
        const nextPage = commentsList.length / 5 + 1;
        let newComments;
        if (replyTo) {
          const { data, error } = await getCommentReplies(
            comments[0].bitId,
            replyTo,
            nextPage
          );
          newComments = data || [];
        } else {
          const { data, error } = await getBitComments(
            comments[0].bitId,
            nextPage
          );
          newComments = data || [];
        }
        if (newComments.length < 5) {
          setIsMoreAvailable(false);
        }
        setCommentsList((prev) => [...prev, ...newComments]);
      }
    } finally {
      isLoading.onFalse();
    }
  };

  return (
    <div
      className={cn('ml-0', {
        'md:ml-10 ml-6': level === 1,
      })}
    >
      {commentsList.map((comment) => (
        <div key={comment.id}>
          <CommentItem
            comment={comment}
            level={level}
            onCommentPosted={onCommentPosted}
          />

          {comment.replies && comment.replies.length > 0 && (
            <Comments
              comments={comment.replies}
              level={level + 1}
              onCommentPosted={onCommentPosted}
              replyTo={comment.id}
            />
          )}
          {level === 0 && (
            <hr className="border-t border-gray-200 md:mt-4 mt-2 w-full" />
          )}
        </div>
      ))}
      {isMoreAvailable && (
        <div className="flex justify-center mt-4">
          <Button
            variant="ghost"
            className="rounded-full bg-bg-gray hover:bg-bg-dark-gray ease-in-out transition-colors duration-200 font-normal md:text-sm"
            onClick={handleMoreComments}
          >
            {isLoading.value ? <Spinner /> : 'Show more'}
          </Button>
        </div>
      )}
    </div>
  );
};
